import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "react-simple-i18n";
import { alpha, styled } from "@mui/material/styles";
import { Avatar, IconButton, Skeleton, Stack, Typography } from "@mui/material";

import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import Settings from '@mui/icons-material/Settings';
import Logout from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import { GlobalSpacing } from "./styles";
import { Drawer } from "../drawer/drawer";
import { menuItems } from "./header.config";
import { Divider } from "../divider/divider";
import { FirstButton } from "../buttons/buttons";
import { initUserData, useGlobalContext } from "../../provider";
import { FirstOutlineButton } from "../buttons/buttons";
import { CustomMenuItem, Menu } from "../menu/menu";
import { useCustomNavigate } from "../../provider/navigate";
import { restApi } from "../../provider/restApi";
import { LanguageMenu } from "./language";
import { capitalizeFirstLetter } from "../../utils";
import { getLogoImage } from "../../config/config";

const initData = {
	loading: false,
	authToken: "",
	loginStatus: false,
	userData: initUserData,
}

const Header = () => {
	const { t } = useI18n();
	const { navigate, customNavigate } = useCustomNavigate();
	const [mobileOpen, setMobileOpen] = useState(false);
	const [globalState]: GlobalContextType = useGlobalContext();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [globalStatus, { dispatch }]: GlobalContextType = useGlobalContext();

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	}

	const onOpenUserMenu = (e: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(e.currentTarget);
	}

	const onCloseUserMenu = () => {
		setAnchorEl(null);
	}

	const onLogout = async () => {
		try {
			dispatch({ loading: true });
			await restApi.logOut();
			localStorage.setItem("isAuthorized","false");
			navigate("/");
			dispatch(initData);
		} catch (err: any) {
			navigate("/");
			dispatch(initData);
		}
	}

	return (
		<HeaderWrapper>
			<HeaderContainer>
				{!!globalState.projectName && (
					<LogoSection to="/">
						<img src={getLogoImage(globalState.logoImage)} alt="flash-transfer" />
						<Typography variant="h5">{globalState.projectName}</Typography>
					</LogoSection>
				)}

				{!globalState.projectName && (
					<LogoSection to="/">
						<Skeleton variant="circular" height={50} width={50} />
						<Skeleton variant="rounded" width={120} height={20} />
					</LogoSection>
				)}

				{/* Right Section - Auth Buttons */}
				<RightSection>
					<LanguageMenu />

					{!globalState.loginStatus && (
						<React.Fragment>
							<FirstOutlineButton onClick={customNavigate("/login")}>
								<Typography variant="body1">{t("buttons.login")}</Typography>
							</FirstOutlineButton>

							<FirstButton onClick={customNavigate("/register")}>
								<Typography variant="body1">{t("buttons.signup")}</Typography>
							</FirstButton>
						</React.Fragment>
					)}

					{globalState.loginStatus && (
						<React.Fragment>
							<UserAvatar onClick={onOpenUserMenu}>
								<Avatar src={globalState.userData.image} alt={capitalizeFirstLetter(globalState.userData.name)} />
							</UserAvatar>

							<Menu anchorEl={anchorEl}
								open={Boolean(anchorEl)}
								onClose={onCloseUserMenu}
								onClick={onCloseUserMenu}
							>
								<CustomMenuItem onClick={customNavigate("/profile")}>
									<Avatar />
									<Typography variant="body2">{t("header.menu.profile")}</Typography>
								</CustomMenuItem>

								{globalStatus.userData.userType === "instructor" && (
									<CustomMenuItem onClick={customNavigate("/newCourse")}>
										<AddCircleOutlineIcon fontSize="small" />
										<Typography variant="body2">{t("header.menu.createCourse")}</Typography>
									</CustomMenuItem>
								)}

								<Divider />

								<CustomMenuItem onClick={customNavigate("/settings")}>
									<Settings fontSize="small" />
									<Typography variant="body2">{t("header.menu.settings")}</Typography>
								</CustomMenuItem>

								<CustomMenuItem onClick={onLogout}>
									<Logout fontSize="small" />
									<Typography variant="body2">{t("buttons.logout")}</Typography>
								</CustomMenuItem>
							</Menu>
						</React.Fragment>
					)}
				</RightSection>

				{/* Mobile Menu Button */}
				<MobileMenuButton aria-label="open menu" onClick={handleDrawerToggle}>
					<MenuIcon />
				</MobileMenuButton>
			</HeaderContainer>

			{/* Mobile Menu Drawer */}
			<Drawer anchor="right"
				open={mobileOpen}
				onClose={handleDrawerToggle}
				ModalProps={{ keepMounted: true }}
			>
				<MobileMenuContainer>
					<MobileMenuHeader>
						{!!globalState.projectName && (
							<LogoSection to="/">
								<img src={getLogoImage(globalState.logoImage)} alt="flash-transfer" />
								<Typography variant="h5">{globalState.projectName}</Typography>
							</LogoSection>
						)}

						{!globalState.projectName && (
							<LogoSection to="/">
								<Skeleton variant="circular" height={40} width={40} />
								<Skeleton variant="rounded" width={100} height={20} />
							</LogoSection>
						)}

						<IconButton onClick={handleDrawerToggle}>
							<CloseIcon />
						</IconButton>
					</MobileMenuHeader>

					<Divider />

					<MobileMenuItems>
						{menuItems.map((item, index) => (
							<MenuItem key={index} href={item.link} onClick={handleDrawerToggle}>
								<Typography variant="body1">{t(`header.${item.label}`)}</Typography>
							</MenuItem>
						))}
					</MobileMenuItems>

					<Divider />

					<MobileMenuItems>
						<MenuItem onClick={customNavigate("/profile")}>
							<AccountCircleIcon fontSize="small" />
							<Typography variant="body2">{t("header.menu.profile")}</Typography>
						</MenuItem>

						{globalStatus.userData.userType === "instructor" && (
							<MenuItem onClick={customNavigate("/newCourse")}>
								<AddCircleOutlineIcon fontSize="small" />
								<Typography variant="body2">{t("header.menu.createCourse")}</Typography>
							</MenuItem>
						)}

						<MenuItem onClick={customNavigate("/settings")}>
							<Settings fontSize="small" />
							<Typography variant="body2">{t("header.menu.settings")}</Typography>
						</MenuItem>

						<MenuItem onClick={onLogout}>
							<Logout fontSize="small" />
							<Typography variant="body2">{t("buttons.logout")}</Typography>
						</MenuItem>
					</MobileMenuItems>

					<Divider />

					{globalState.loginStatus && (
						<LanguageMenu onClose={handleDrawerToggle} />
					)}

					{!globalState.loginStatus && (
						<MobileAuthButtons>
							<FirstOutlineButton fullWidth>
								<Typography variant="body1">{t("buttons.login")}</Typography>
							</FirstOutlineButton>

							<FirstButton fullWidth>
								<Typography variant="body1">{t("buttons.signup")}</Typography>
							</FirstButton>
						</MobileAuthButtons>
					)}
				</MobileMenuContainer>
			</Drawer>
		</HeaderWrapper>
	)
}

const HeaderWrapper = styled(GlobalSpacing)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",

	height: 70,
	backgroundColor: theme.palette.common.mainBg,
	boxShadow: `0px 2px 5px ${theme.palette.common.firstBorder}`,

	[theme.breakpoints.down("lg")]: {
		height: 60,
	}
}))

const HeaderContainer = styled(Stack)(({ theme }) => ({
	gap: theme.spacing(3),
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "space-between",
}))

const LogoSection = styled(Link)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	textDecoration: "none",
	gap: theme.spacing(1.5),

	"& img": {
		height: "50px",
	},

	[theme.breakpoints.down('lg')]: {
		"& img": {
			height: "40px",
		},

		h5: {
			display: "none",
			fontWeight: 600,
		},
	},
}))

const RightSection = styled(Stack)(({ theme }) => ({
	flexDirection: "row",
	gap: theme.spacing(2),
	paddingTop: theme.spacing(1),

	p: {
		color: theme.palette.common.secondColor,
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
		display: "none",
	}
}))

// Mobile specific styles
const MobileMenuButton = styled(IconButton)(({ theme }) => ({
	display: 'none',
	borderRadius: "50%",
	padding: theme.spacing(1.1),
	backgroundColor: "transparent",

	svg: {
		color: theme.palette.common.firstColor,
	},

	[theme.breakpoints.down('md')]: {
		display: 'flex',
	},
}))

const MobileMenuContainer = styled(Stack)(({ theme }) => ({
	height: '100%',
	maxWidth: "100%",
	overflowY: "auto",

	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(3),
	padding: theme.spacing(3),
}))

const MobileMenuHeader = styled(Stack)(({ theme }) => ({
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'space-between',

	svg: {
		color: theme.palette.common.firstColor,
	},
}))

const MobileMenuItems = styled(Stack)(({ theme }) => ({
	gap: theme.spacing(2),
}))

const MenuItem = styled("a")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	gap: theme.spacing(2),
	textDecoration: "none",

	p: {
		opacity: 0.8,
		fontWeight: 500,
		color: theme.palette.common.firstColor,
	},

	svg: {
		color: alpha(theme.palette.common.firstColor, 0.75),
	},

	"&:hover": {
		p: {
			opacity: 1,
			color: theme.palette.common.firstColor,
		},
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
	}
}))

const MobileAuthButtons = styled(Stack)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'row',
	gap: theme.spacing(2),

	"h1, h2, h3, h4, h5, h6, p, span, a": {
		color: theme.palette.common.firstColor,
	},
}))

const UserAvatar = styled(Stack)(({ theme }) => ({
	width: 45,
	height: 45,

	cursor: "pointer",
	overflow: "hidden",
	borderRadius: "50%",
	border: `3px solid ${theme.palette.common.white}`,
	boxShadow: `0px 1px 5px 0px ${theme.palette.common.firstBorder}`,
	transition: 'box-shadow 0.3s ease',

	".MuiAvatar-root, .MuiAvatar-root img": {
		width: "100%",
		height: "100%",
		borderRadius: "50%",
	},

	"&:hover": {
		boxShadow: `0px 1px 5px 2px ${theme.palette.common.firstBorder}`,
	}
}))

export { Header }